import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { BookOpen, FileText, Plus, ChevronLeft, Eye, EyeOff, Download, Upload, RefreshCw, Table, Trash2, Pencil, X } from 'lucide-react';
import { ApiService } from '../services/api';
import type { Journal, Entry, Submission, User, JournalStats, RubricGrid } from '../types';
import { RichTextEditor } from '../components/RichTextEditor';
import { RubricBuilder } from '../components/RubricBuilder';
import { GradingModal } from '../components/GradingModal';
import { StatsPanel } from '../components/StatsPanel';
import { DEFAULT_RUBRIC } from '../types';

interface ReportRow {
  student_name: string;
  student_email: string;
  student_usn: string;
  student_program: string;
  student_cohort: string;
  entry_title: string;
  max_score: number;
  submitted_at: string;
  word_count: number;
  student_response: string;
  ai_score: number | string;
  percentage: number | string;
  grade_letter: string;
  llm_confidence: number | string;
  ai_feedback: string;
  strengths: string;
  growth: string;
  teacher_grade: number | string;
  teacher_comments: string;
  status: string;
}

interface EntryFormState {
  title: string;
  instructions: string;
  due_date: string;
  min_words?: number;
  max_words?: number;
  max_attempts?: number;
  max_score?: number;
  allow_resubmission: boolean;
  rubric: RubricGrid;
}

export const TeacherDashboard: React.FC = () => {
  const [journals, setJournals] = useState<Journal[]>([]);
  const [selectedJournal, setSelectedJournal] = useState<Journal | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [students, setStudents] = useState<User[]>([]);
  const [allStudents, setAllStudents] = useState<User[]>([]);
  const [stats, setStats] = useState<JournalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'entries' | 'students' | 'reports'>('entries');
  
  // Report data
  const [reportData, setReportData] = useState<ReportRow[]>([]);
  const [reportLoading, setReportLoading] = useState(false);
  
  // Modals
  const [showCreateJournal, setShowCreateJournal] = useState(false);
  const [showCreateEntry, setShowCreateEntry] = useState(false);
  const [showEditEntry, setShowEditEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState<Entry | null>(null);
  const [showEnrollStudent, setShowEnrollStudent] = useState(false);
  const [showBulkEnroll, setShowBulkEnroll] = useState(false);
  const [gradingSubmission, setGradingSubmission] = useState<{ submission: Submission; entry: Entry } | null>(null);
  const [bulkEnrollResult, setBulkEnrollResult] = useState<any>(null);
  const [bulkEnrolling, setBulkEnrolling] = useState(false);
  const bulkEnrollFileRef = useRef<HTMLInputElement>(null);
  
  // Forms
  const parseOptionalNumber = (value: string): number | undefined => {
    return value === '' ? undefined : parseInt(value, 10);
  };

  const [journalForm, setJournalForm] = useState({ title: '', description: '' });
  const [entryForm, setEntryForm] = useState<EntryFormState>({
    title: '',
    instructions: '',
    due_date: '',
    min_words: 100,
    max_words: 1000,
    max_attempts: 1,
    max_score: 100,
    allow_resubmission: false,
    rubric: DEFAULT_RUBRIC,
  });
  const [editEntryForm, setEditEntryForm] = useState<EntryFormState>({
    title: '',
    instructions: '',
    due_date: '',
    min_words: 100,
    max_words: 1000,
    max_attempts: 1,
    max_score: 100,
    allow_resubmission: false,
    rubric: DEFAULT_RUBRIC,
  });
  const [selectedStudentId, setSelectedStudentId] = useState('');
  
  // Submissions
  const [entrySubmissions, setEntrySubmissions] = useState<Record<string, Submission[]>>({});
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);

  useEffect(() => { loadJournals(); loadAllStudents(); }, []);

  useEffect(() => {
    if (selectedJournal) {
      loadJournalData(selectedJournal.id);
    }
  }, [selectedJournal]);

  useEffect(() => {
    if (activeTab === 'reports' && selectedJournal) {
      loadReportData();
    }
  }, [activeTab, selectedJournal]);

  const loadJournals = async () => {
    try {
      const data = await ApiService.getJournals();
      setJournals(data);
    } catch { toast.error('Failed to load journals'); }
    finally { setLoading(false); }
  };

  const loadAllStudents = async () => {
    try {
      const data = await ApiService.getUsers();
      setAllStudents(data.filter((u: User) => u.role === 'student'));
    } catch { console.error('Failed to load students'); }
  };

  const loadJournalData = async (journalId: string) => {
    try {
      const [entriesData, studentsData, statsData] = await Promise.all([
        ApiService.getEntries(journalId),
        ApiService.getEnrolledStudents(journalId),
        ApiService.getJournalStats(journalId),
      ]);
      setEntries(entriesData);
      setStudents(studentsData);
      setStats(statsData);
    } catch { toast.error('Failed to load journal data'); }
  };

  const loadReportData = async () => {
    if (!selectedJournal) return;
    setReportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const baseUrl = import.meta.env.VITE_API_URL || 'https://flex-api.iiitdwd.com/api';
      const response = await fetch(`${baseUrl}/journals/${selectedJournal.id}/reports/data`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to load report');
      }
      const data = await response.json();
      setReportData(data.data || []);
    } catch (error: any) {
      console.error('Failed to load report data:', error);
      toast.error(error.message || 'Failed to load report data');
    } finally {
      setReportLoading(false);
    }
  };

  const handleCreateJournal = async () => {
    try {
      await ApiService.createJournal(journalForm);
      toast.success('Journal created!');
      setShowCreateJournal(false);
      setJournalForm({ title: '', description: '' });
      loadJournals();
    } catch { toast.error('Failed to create journal'); }
  };

  const handleCreateEntry = async () => {
    if (!selectedJournal) return;
    try {
      await ApiService.createEntry(selectedJournal.id, entryForm);
      toast.success('Entry created!');
      setShowCreateEntry(false);
      setEntryForm({ 
        title: '', 
        instructions: '', 
        due_date: '', 
        min_words: 100, 
        max_words: 1000,
        max_attempts: 1, 
        max_score: 100, 
        allow_resubmission: false, 
        rubric: DEFAULT_RUBRIC 
      });
      loadJournalData(selectedJournal.id);
    } catch { toast.error('Failed to create entry'); }
  };

  const handleEditEntryOpen = (entry: Entry) => {
    setEditingEntry(entry);
    setEditEntryForm({
      title: entry.title,
      instructions: entry.instructions,
      due_date: entry.due_date ? entry.due_date.slice(0, 16) : '',
      min_words: entry.min_words || 100,
      max_words: entry.max_words || 1000,
      max_attempts: entry.max_attempts || 3,
      max_score: entry.max_score || 100,
      allow_resubmission: entry.allow_resubmission ?? false,
      rubric: entry.rubric || DEFAULT_RUBRIC,
    });
    setShowEditEntry(true);
  };

  const handleEditEntry = async () => {
    if (!selectedJournal || !editingEntry) return;
    try {
      await ApiService.updateEntry(selectedJournal.id, editingEntry.id, editEntryForm);
      toast.success('Entry updated!');
      setShowEditEntry(false);
      setEditingEntry(null);
      loadJournalData(selectedJournal.id);
    } catch { toast.error('Failed to update entry'); }
  };

  const handleDeleteEntry = async (entry: Entry) => {
    if (!confirm(`Delete "${entry.title}"? This will also delete all its submissions.`)) return;
    try {
      await ApiService.deleteEntry(entry.id);
      toast.success('Entry deleted');
      loadJournalData(selectedJournal!.id);
    } catch { toast.error('Failed to delete entry'); }
  };

  const handleEnrollStudent = async () => {
    if (!selectedJournal || !selectedStudentId) return;
    try {
      await ApiService.enrollStudent(selectedJournal.id, selectedStudentId);
      toast.success('Student enrolled!');
      setShowEnrollStudent(false);
      setSelectedStudentId('');
      loadJournalData(selectedJournal.id);
    } catch { toast.error('Failed to enroll student'); }
  };

  const loadSubmissions = async (entryId: string) => {
    try {
      const data = await ApiService.getSubmissions(entryId);
      setEntrySubmissions(prev => ({ ...prev, [entryId]: data }));
      setExpandedEntry(entryId);
    } catch { toast.error('Failed to load submissions'); }
  };

  const handleGenerateReport = async (format: 'xlsx' | 'csv' = 'csv') => {
    try {
      toast.info(`Generating ${format.toUpperCase()} report...`);
      const token = localStorage.getItem('token');
      const baseUrl = import.meta.env.VITE_API_URL || 'https://flex-api.iiitdwd.com/api';
      const response = await fetch(`${baseUrl}/journals/${selectedJournal!.id}/reports/download?format=${format}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to generate report');
      const arrayBuffer = await response.arrayBuffer();
      const mimeType = format === 'xlsx' 
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv;charset=utf-8';
      const blob = new Blob([arrayBuffer], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `journal_report_${selectedJournal!.title.replace(/[^a-zA-Z0-9]/g, '_')}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded!');
    } catch (error) { 
      toast.error('Failed to generate report'); 
      console.error(error);
    }
  };

  const handleBulkEnroll = async (e: React.FormEvent) => {
    e.preventDefault();
    const file = bulkEnrollFileRef.current?.files?.[0];
    if (!file || !selectedJournal) return;
    setBulkEnrolling(true);
    setBulkEnrollResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://flex-api.iiitdwd.com/api'}/journals/${selectedJournal.id}/bulk-enroll`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      const result = await response.json();
      setBulkEnrollResult(result);
      if (result.enrolled > 0) {
        toast.success(`Enrolled ${result.enrolled} students!`);
        loadJournalData(selectedJournal.id);
      }
    } catch (error) {
      toast.error('Bulk enrollment failed');
    } finally {
      setBulkEnrolling(false);
    }
  };

  const toggleEntryVisibility = async (entry: Entry) => {
    if (!selectedJournal) return;
    try {
      await ApiService.updateEntry(selectedJournal.id, entry.id, { is_visible: !entry.is_visible });
      toast.success(entry.is_visible ? 'Entry hidden' : 'Entry visible');
      loadJournalData(selectedJournal.id);
    } catch { toast.error('Failed to update entry'); }
  };

  const availableStudents = allStudents.filter(s => !students.find(enrolled => enrolled.id === s.id));

  if (loading && journals.length === 0) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      {!selectedJournal ? (
        <>
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-slate-800">My Journals</h2>
            <button onClick={() => setShowCreateJournal(true)} className="px-4 py-2 bg-[#1e3a5f] hover:bg-[#152a45] text-white text-sm font-medium rounded-md flex items-center gap-2">
              <Plus className="w-4 h-4" />New Journal
            </button>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {journals.map(journal => (
              <div key={journal.id} onClick={() => setSelectedJournal(journal)} className="bg-white rounded-lg border border-slate-200 p-6 hover:shadow-md transition-all cursor-pointer">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-blue-50 rounded-lg"><BookOpen className="w-6 h-6 text-blue-600" /></div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{journal.title}</h3>
                    <p className="text-sm text-slate-500">Created {new Date(journal.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <p className="text-slate-600 text-sm line-clamp-2">{journal.description || 'No description'}</p>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <button onClick={() => setSelectedJournal(null)} className="p-2 hover:bg-slate-200 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
            <div>
              <h2 className="text-xl font-semibold text-slate-800">{selectedJournal.title}</h2>
              <p className="text-slate-600 text-sm">{selectedJournal.description}</p>
            </div>
          </div>

          {stats && <StatsPanel stats={stats} />}

          <div className="flex gap-2 border-b border-slate-200">
            {(['entries', 'students', 'reports'] as const).map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 font-medium capitalize ${activeTab === tab ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-600 hover:text-slate-800'}`}>
                {tab}
              </button>
            ))}
          </div>

          {/* Entries Tab */}
          {activeTab === 'entries' && (
            <div className="space-y-4">
              <button onClick={() => setShowCreateEntry(true)} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg flex items-center gap-2">
                <Plus className="w-4 h-4" />New Entry
              </button>
              
              {entries.map(entry => (
                <div key={entry.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <div>
                        <h4 className="font-medium text-slate-800">{entry.title}</h4>
                        <p className="text-sm text-slate-500">
                          Due: {entry.due_date ? new Date(entry.due_date).toLocaleDateString() : 'No due date'} | 
                          Max Score: {entry.max_score || 100} | 
                          Words: {entry.min_words || 100}-{entry.max_words || 1000}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleEntryVisibility(entry)} className={`p-2 rounded-lg ${entry.is_visible ? 'text-green-600 hover:bg-green-50' : 'text-slate-400 hover:bg-slate-50'}`} title={entry.is_visible ? 'Hide from students' : 'Show to students'}>
                        {entry.is_visible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                      </button>
                      <button onClick={() => handleEditEntryOpen(entry)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit entry">
                        <Pencil className="w-5 h-5" />
                      </button>
                      <button onClick={() => handleDeleteEntry(entry)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Delete entry">
                        <Trash2 className="w-5 h-5" />
                      </button>
                      <button onClick={() => loadSubmissions(entry.id)} className="px-3 py-1 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg">
                        View Submissions
                      </button>
                    </div>
                  </div>
                  
                  {expandedEntry === entry.id && entrySubmissions[entry.id] && (
                    <div className="border-t border-slate-100 bg-slate-50 p-4">
                      <h5 className="font-medium text-slate-700 mb-3">Submissions ({entrySubmissions[entry.id].length})</h5>
                      <div className="space-y-2">
                        {entrySubmissions[entry.id].length === 0 ? (
                          <p className="text-slate-500 text-sm">No submissions yet</p>
                        ) : (
                          entrySubmissions[entry.id].map(sub => (
                            <div key={sub.id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                              <div>
                                <p className="font-medium text-slate-800">
                                  {sub.student_name}
                                  {sub.student_usn && <span className="text-slate-500 font-normal ml-2">({sub.student_usn})</span>}
                                </p>
                                <p className="text-sm text-slate-500">
                                  {new Date(sub.submitted_at).toLocaleString()} | {sub.word_count} words |
                                  Score: {sub.feedback?.ai_feedback?.total_score ?? '-'}/{entry.max_score || 100}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  sub.status === 'graded' ? 'bg-green-100 text-green-700' :
                                  sub.status === 'feedback_ready' ? 'bg-blue-100 text-blue-700' :
                                  sub.status === 'pending_review' ? 'bg-orange-100 text-orange-700' :
                                  sub.status === 'error' ? 'bg-red-100 text-red-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {sub.status === 'pending_review' ? '⚠ Needs Manual Review' : sub.status}
                                </span>
                                <button onClick={() => setGradingSubmission({ submission: sub, entry })} className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg">
                                  Review
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Students Tab */}
          {activeTab === 'students' && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <button onClick={() => setShowEnrollStudent(true)} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg flex items-center gap-2">
                  <Plus className="w-4 h-4" />Enroll Student
                </button>
                <button onClick={() => setShowBulkEnroll(true)} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg flex items-center gap-2">
                  <Upload className="w-4 h-4" />Bulk Enroll
                </button>
              </div>
              
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Name</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Email</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">USN</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Program</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Cohort</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Enrolled</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map(student => (
                        <tr key={student.id} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium text-slate-800">{student.name}</td>
                          <td className="px-4 py-3 text-slate-600">{student.email}</td>
                          <td className="px-4 py-3 text-slate-600">{student.usn || <span className="text-slate-300">-</span>}</td>
                          <td className="px-4 py-3 text-slate-600">{student.program || <span className="text-slate-300">-</span>}</td>
                          <td className="px-4 py-3 text-slate-600">{student.cohort || <span className="text-slate-300">-</span>}</td>
                          <td className="px-4 py-3 text-slate-500">{(student as any).enrolled_at ? new Date((student as any).enrolled_at).toLocaleDateString() : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Reports Tab */}
          {activeTab === 'reports' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Table className="w-6 h-6 text-blue-600" />
                    <h3 className="text-lg font-semibold text-slate-800">Journal Report</h3>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={loadReportData} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-lg flex items-center gap-2">
                      <RefreshCw className={`w-4 h-4 ${reportLoading ? 'animate-spin' : ''}`} />Refresh
                    </button>
                    <button onClick={() => handleGenerateReport('xlsx')} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg flex items-center gap-2">
                      <Download className="w-4 h-4" />Excel
                    </button>
                    <button onClick={() => handleGenerateReport('csv')} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg flex items-center gap-2">
                      <Download className="w-4 h-4" />CSV
                    </button>
                  </div>
                </div>

                {reportLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : reportData.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No submissions yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Student</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Email</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">USN</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Program</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Cohort</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Entry</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Submitted</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Words</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">AI Score</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">%</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Grade</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Confidence</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Teacher Grade</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.map((row, idx) => (
                          <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50">
                            <td className="px-3 py-2 font-medium text-slate-800">{row.student_name}</td>
                            <td className="px-3 py-2 text-slate-600 text-xs">{row.student_email}</td>
                            <td className="px-3 py-2 text-slate-600">{row.student_usn || '-'}</td>
                            <td className="px-3 py-2 text-slate-600">{row.student_program || '-'}</td>
                            <td className="px-3 py-2 text-slate-600">{row.student_cohort || '-'}</td>
                            <td className="px-3 py-2 text-slate-700">{row.entry_title}</td>
                            <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{row.submitted_at || '-'}</td>
                            <td className="px-3 py-2 text-slate-600">{row.word_count || '-'}</td>
                            <td className="px-3 py-2 text-slate-800 font-medium">{row.ai_score !== '' ? `${row.ai_score}/${row.max_score}` : '-'}</td>
                            <td className="px-3 py-2 text-slate-600">{row.percentage ? `${row.percentage}%` : '-'}</td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-1 text-xs font-bold rounded ${
                                row.grade_letter?.startsWith('A') ? 'bg-green-100 text-green-700' :
                                row.grade_letter?.startsWith('B') ? 'bg-blue-100 text-blue-700' :
                                row.grade_letter?.startsWith('C') ? 'bg-yellow-100 text-yellow-700' :
                                row.grade_letter?.startsWith('D') ? 'bg-orange-100 text-orange-700' :
                                row.grade_letter?.startsWith('F') ? 'bg-red-100 text-red-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>{row.grade_letter || '-'}</span>
                            </td>
                            <td className="px-3 py-2 text-slate-600">{row.llm_confidence ? `${row.llm_confidence}%` : '-'}</td>
                            <td className="px-3 py-2 text-slate-800 font-medium">{row.teacher_grade !== '' ? `${row.teacher_grade}/${row.max_score}` : '-'}</td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                row.status === 'graded' ? 'bg-green-100 text-green-700' :
                                row.status === 'feedback_ready' ? 'bg-blue-100 text-blue-700' :
                                row.status === 'Not Submitted' ? 'bg-slate-100 text-slate-500' :
                                row.status === 'pending_review' ? 'bg-orange-100 text-orange-700' :
                                row.status === 'error' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>{row.status === 'pending_review' ? '⚠ Needs Review' : row.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {reportData.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">Detailed Feedback View</h3>
                  <div className="space-y-4">
                    {reportData.filter(r => r.status !== 'Not Submitted').map((row, idx) => (
                      <details key={idx} className="border border-slate-200 rounded-lg">
                        <summary className="px-4 py-3 cursor-pointer hover:bg-slate-50 font-medium text-slate-800">
                          {row.student_name} - {row.entry_title} ({row.grade_letter || 'Not Graded'})
                        </summary>
                        <div className="px-4 py-3 border-t border-slate-200 space-y-3 text-sm">
                          <div className="flex flex-wrap gap-4 text-slate-600 bg-slate-50 p-2 rounded">
                            <span><strong>Email:</strong> {row.student_email}</span>
                            {row.student_usn && <span><strong>USN:</strong> {row.student_usn}</span>}
                            {row.student_program && <span><strong>Program:</strong> {row.student_program}</span>}
                            {row.student_cohort && <span><strong>Cohort:</strong> {row.student_cohort}</span>}
                          </div>
                          <div>
                            <span className="font-medium text-slate-700">Student Response:</span>
                            <p className="mt-1 text-slate-600 whitespace-pre-wrap bg-slate-50 p-3 rounded max-h-40 overflow-y-auto">{row.student_response || 'No response'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-slate-700">AI Feedback:</span>
                            <p className="mt-1 text-slate-600 whitespace-pre-wrap bg-blue-50 p-3 rounded">{row.ai_feedback || 'No feedback'}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <span className="font-medium text-slate-700">Strengths:</span>
                              <p className="mt-1 text-green-700 bg-green-50 p-2 rounded">{row.strengths || '-'}</p>
                            </div>
                            <div>
                              <span className="font-medium text-slate-700">Growth Opportunities:</span>
                              <p className="mt-1 text-orange-700 bg-orange-50 p-2 rounded">{row.growth || '-'}</p>
                            </div>
                          </div>
                          {row.teacher_comments && (
                            <div>
                              <span className="font-medium text-slate-700">Teacher Comments:</span>
                              <p className="mt-1 text-slate-600 bg-purple-50 p-3 rounded">{row.teacher_comments}</p>
                            </div>
                          )}
                        </div>
                      </details>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Create Journal Modal */}
      {showCreateJournal && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 py-8">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-slate-800">Create Journal</h3>
                <button onClick={() => setShowCreateJournal(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                  <input type="text" value={journalForm.title} onChange={(e) => setJournalForm({ ...journalForm, title: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" placeholder="e.g., Biology 101 Reflections" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea value={journalForm.description} onChange={(e) => setJournalForm({ ...journalForm, description: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" rows={3} placeholder="Brief description..." />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowCreateJournal(false)} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                <button onClick={handleCreateJournal} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Create</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Entry Modal */}
      {showCreateEntry && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 py-8">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-slate-800">Create Entry</h3>
                <button onClick={() => setShowCreateEntry(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                    <input type="text" value={entryForm.title} onChange={(e) => setEntryForm({ ...entryForm, title: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" placeholder="Entry title" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
                    <input type="datetime-local" value={entryForm.due_date} onChange={(e) => setEntryForm({ ...entryForm, due_date: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Max Score</label>
                    <input type="number" value={entryForm.max_score ?? ''} onChange={(e) => setEntryForm({ ...entryForm, max_score: parseOptionalNumber(e.target.value) })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" min="1" max="1000" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Min Words</label>
                    <input type="number" value={entryForm.min_words ?? ''} onChange={(e) => setEntryForm({ ...entryForm, min_words: parseOptionalNumber(e.target.value) })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" min="10" max="10000" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Max Words</label>
                    <input type="number" value={entryForm.max_words ?? ''} onChange={(e) => setEntryForm({ ...entryForm, max_words: parseOptionalNumber(e.target.value) })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" min="100" max="50000" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Max Attempts</label>
                    <input type="number" value={entryForm.max_attempts ?? ''} onChange={(e) => setEntryForm({ ...entryForm, max_attempts: parseOptionalNumber(e.target.value) })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" min="1" max="10" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="allow_resubmission" checked={entryForm.allow_resubmission} onChange={(e) => setEntryForm({ ...entryForm, allow_resubmission: e.target.checked })} className="w-4 h-4 text-blue-600 rounded" />
                    <label htmlFor="allow_resubmission" className="text-sm font-medium text-slate-700">Allow Resubmission</label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Instructions</label>
                  <RichTextEditor value={entryForm.instructions} onChange={(v) => setEntryForm({ ...entryForm, instructions: v })} placeholder="Write detailed instructions..." />
                </div>
                <RubricBuilder rubric={entryForm.rubric} onChange={(r) => setEntryForm({ ...entryForm, rubric: r })} />
              </div>
              <div className="flex gap-3 mt-6 pt-4 border-t border-slate-200">
                <button onClick={() => setShowCreateEntry(false)} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                <button onClick={handleCreateEntry} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Create Entry</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Entry Modal */}
      {showEditEntry && editingEntry && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 py-8">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-slate-800">Edit Entry</h3>
                <button onClick={() => { setShowEditEntry(false); setEditingEntry(null); }} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                    <input type="text" value={editEntryForm.title} onChange={(e) => setEditEntryForm({ ...editEntryForm, title: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
                    <input type="datetime-local" value={editEntryForm.due_date} onChange={(e) => setEditEntryForm({ ...editEntryForm, due_date: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Max Score</label>
                    <input type="number" value={editEntryForm.max_score ?? ''} onChange={(e) => setEditEntryForm({ ...editEntryForm, max_score: parseOptionalNumber(e.target.value) })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" min="1" max="1000" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Min Words</label>
                    <input type="number" value={editEntryForm.min_words ?? ''} onChange={(e) => setEditEntryForm({ ...editEntryForm, min_words: parseOptionalNumber(e.target.value) })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" min="10" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Max Words</label>
                    <input type="number" value={editEntryForm.max_words ?? ''} onChange={(e) => setEditEntryForm({ ...editEntryForm, max_words: parseOptionalNumber(e.target.value) })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" min="100" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Max Attempts</label>
                    <input type="number" value={editEntryForm.max_attempts ?? ''} onChange={(e) => setEditEntryForm({ ...editEntryForm, max_attempts: parseOptionalNumber(e.target.value) })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" min="1" max="10" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="edit_allow_resubmission" checked={editEntryForm.allow_resubmission} onChange={(e) => setEditEntryForm({ ...editEntryForm, allow_resubmission: e.target.checked })} className="w-4 h-4 text-blue-600 rounded" />
                    <label htmlFor="edit_allow_resubmission" className="text-sm font-medium text-slate-700">Allow Resubmission</label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Instructions</label>
                  <RichTextEditor value={editEntryForm.instructions} onChange={(v) => setEditEntryForm({ ...editEntryForm, instructions: v })} placeholder="Write detailed instructions..." />
                </div>
                <RubricBuilder rubric={editEntryForm.rubric} onChange={(r) => setEditEntryForm({ ...editEntryForm, rubric: r })} />
              </div>
              <div className="flex gap-3 mt-6 pt-4 border-t border-slate-200">
                <button onClick={() => { setShowEditEntry(false); setEditingEntry(null); }} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                <button onClick={handleEditEntry} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enroll Student Modal */}
      {showEnrollStudent && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 py-8">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-slate-800">Enroll Student</h3>
                <button onClick={() => setShowEnrollStudent(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
              </div>
              <select value={selectedStudentId} onChange={(e) => setSelectedStudentId(e.target.value)} className="w-full px-4 py-2 border border-slate-200 rounded-lg">
                <option value="">Select a student...</option>
                {availableStudents.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.email}){s.usn ? ` - ${s.usn}` : ''}
                  </option>
                ))}
              </select>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowEnrollStudent(false)} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                <button onClick={handleEnrollStudent} disabled={!selectedStudentId} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50">Enroll</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Enroll Modal */}
      {showBulkEnroll && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 py-8">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-slate-800">Bulk Enroll Students</h3>
                <button onClick={() => { setShowBulkEnroll(false); setBulkEnrollResult(null); }} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-sm text-slate-600 mb-4">Upload a CSV or Excel file with student emails. Students must already exist in the system.</p>
              <form onSubmit={handleBulkEnroll}>
                <input type="file" ref={bulkEnrollFileRef} accept=".csv,.xlsx,.xls" className="w-full px-4 py-2 border border-slate-200 rounded-lg" required />
                {bulkEnrollResult && (
                  <div className={`mt-4 p-3 rounded-lg ${bulkEnrollResult.errors?.length > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
                    <p className="font-medium">Enrolled: {bulkEnrollResult.enrolled}</p>
                    <p>Skipped: {bulkEnrollResult.skipped}</p>
                    {bulkEnrollResult.errors?.length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-sm text-red-600">Errors ({bulkEnrollResult.errors.length})</summary>
                        <ul className="text-xs text-red-600 mt-1 max-h-32 overflow-y-auto">
                          {bulkEnrollResult.errors.map((e: string, i: number) => <li key={i}>{e}</li>)}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
                <div className="flex gap-3 mt-6">
                  <button type="button" onClick={() => { setShowBulkEnroll(false); setBulkEnrollResult(null); }} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button type="submit" disabled={bulkEnrolling} className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50">
                    {bulkEnrolling ? 'Enrolling...' : 'Upload & Enroll'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Grading Modal */}
      {gradingSubmission && (
        <GradingModal
          submission={gradingSubmission.submission}
          entry={gradingSubmission.entry}
          onClose={() => setGradingSubmission(null)}
          onGraded={() => {
            loadSubmissions(gradingSubmission.entry.id);
            setGradingSubmission(null);
          }}
        />
      )}
    </div>
  );
};